import CategoryCards from '@/components/CategoryCards';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { useSeo } from '@/lib/seo';
import { useJsonLd, buildWebSiteSchema } from '@/lib/jsonld';

type Lang = 'en' | 'pt' | 'de';

function resolveLang(lang: string): Lang {
  if (lang === 'pt' || lang === 'de') return lang;
  return 'en';
}

const HERO_IMAGE = 'https://readdy.ai/api/search-image?query=An%20editorial%20lifestyle%20arrangement%20of%20premium%20fashion%20items%20including%20a%20white%20sneaker%2C%20tan%20leather%20bag%2C%20folded%20hoodie%20and%20sunglasses%20styled%20on%20a%20warm%20concrete%20surface%20with%20soft%20directional%20light%2C%20high%20end%20ecommerce%20photography%2C%20muted%20earth%20tones%20with%20a%20single%20emerald%20green%20accent%2C%20shallow%20depth%20of%20field&width=1600&height=900&seq=hero-bg-01&orientation=landscape&nocache=true';

const popularTags = [
  { label: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20single%20clean%20white%20minimalist%20sneaker%20on%20a%20soft%20grey%20studio%20background%2C%20tiny%20product%20icon%2C%20crisp%20lighting%2C%20simple%20composition&width=100&height=100&seq=tag-shoes-01&orientation=squarish' },
  { label: 'Clothing', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20folded%20soft%20cotton%20t-shirt%20on%20a%20light%20neutral%20background%2C%20tiny%20product%20icon%2C%20soft%20lighting%2C%20simple%20clean%20composition&width=100&height=100&seq=tag-clothing-01&orientation=squarish' },
  { label: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20small%20structured%20leather%20handbag%20on%20a%20warm%20beige%20background%2C%20tiny%20product%20icon%2C%20soft%20lighting%2C%20clean%20minimal%20composition&width=100&height=100&seq=tag-bags-01&orientation=squarish' },
  { label: 'Sneakers', url: 'https://kaspreadsheet.com/category-911-Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20colorful%20athletic%20sneaker%20on%20a%20bright%20background%2C%20tiny%20product%20icon%2C%20crisp%20lighting%2C%20bold%20simple%20composition&width=100&height=100&seq=tag-sneakers-01&orientation=squarish' },
  { label: 'Streetwear', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=An%20oversized%20graphic%20hoodie%20on%20a%20light%20background%2C%20tiny%20product%20icon%2C%20soft%20lighting%2C%20casual%20street%20style%20composition&width=100&height=100&seq=tag-streetwear-01&orientation=squarish' },
  { label: 'Sellers', url: 'https://kaspreadsheet.com', img: 'https://readdy.ai/api/search-image?query=A%20small%20storefront%20with%20a%20shopping%20bag%20icon%20on%20a%20light%20background%2C%20tiny%20product%20icon%2C%20soft%20lighting%2C%20clean%20friendly%20composition&width=100&height=100&seq=tag-sellers-01&orientation=squarish' },
];

export default function Home() {
  const { t, i18n } = useTranslation(['common', 'seo']);
  const lang = resolveLang(i18n.language);

  useSeo({
    title: t('seo:home.metaTitle'),
    description: t('seo:home.metaDesc'),
    keywords: 'cssbuy spreadsheet, cssbuy products, cssbuy sellers, cssbuy guide, cssbuy review, cssbuy shipping',
    canonical: '/',
    ogImage: HERO_IMAGE,
  });

  useJsonLd(buildWebSiteSchema(t('seo:home.metaDesc')));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with product background */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="CSSBuy Spreadsheet Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl">
            <p className="text-emerald-300 text-sm font-semibold uppercase tracking-wider mb-4">
              CSSBuy Spreadsheet Resource Hub
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('common:hero.title')}
            </h1>
            <p className="text-base md:text-lg text-gray-200 mb-8 leading-relaxed max-w-2xl">
              {t('common:hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20"
              >
                {t('common:cta.explore')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg bg-white text-gray-900 font-semibold text-sm hover:bg-gray-100 transition-colors"
              >
                {t('common:cta.buyNow')}
              </a>
            </div>

            <p className="text-sm text-gray-300">
              {t('common:hero.trust')}
            </p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="relative -mt-8 z-10 max-w-3xl mx-auto px-4 md:px-6">
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 px-3">
            <i className="ri-search-line text-gray-400 text-lg" />
            <input
              type="text"
              placeholder={t('common:search.placeholder')}
              className="w-full py-3 text-sm text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
            />
          </div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-3 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap"
          >
            {t('common:cta.search')}
          </a>
        </div>
        <div className="flex items-center gap-3 mt-4 px-1 flex-wrap">
          <span className="text-xs text-gray-500">{t('common:search.popular')}</span>
          <div className="flex flex-wrap gap-2">
            {popularTags.map((tag) => (
              <a
                key={tag.label}
                href={tag.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 pl-1 pr-3 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              >
                <span className="w-6 h-6 rounded-full overflow-hidden bg-white flex items-center justify-center">
                  <img src={tag.img} alt={tag.label} className="w-full h-full object-cover" />
                </span>
                <span className="text-xs font-medium">{tag.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <CategoryCards />

      {/* Spreadsheet Benefits */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {t('common:spreadsheet.title')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base">
              {t('common:spreadsheet.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <i className="ri-search-2-line text-emerald-600 text-xl" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('common:spreadsheet.faster')}</h3>
              <p className="text-sm text-gray-500">{t('common:spreadsheet.fasterDesc')}</p>
            </div>
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <i className="ri-folder-5-line text-emerald-600 text-xl" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('common:spreadsheet.organized')}</h3>
              <p className="text-sm text-gray-500">{t('common:spreadsheet.organizedDesc')}</p>
            </div>
            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-emerald-600 text-xl" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('common:spreadsheet.context')}</h3>
              <p className="text-sm text-gray-500">{t('common:spreadsheet.contextDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Articles / Pillar Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                {t('common:articles.title')}
              </h2>
              <p className="text-gray-500 text-sm md:text-base">
                {t('common:articles.subtitle')}
              </p>
            </div>
            <Link
              to="/article/cssbuy-beginner-guide"
              className="hidden md:flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
            >
              {t('common:cta.viewAll')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group text-left bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img
                    src={article.image}
                    alt={article.title[lang]}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${article.tagClass}`}>
                      {article.tag[lang]}
                    </span>
                    <span className="text-xs text-gray-400">{article.readTime[lang]}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors line-clamp-2">
                    {article.title[lang]}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {article.excerpt[lang]}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-6 md:hidden">
            <Link
              to="/article/cssbuy-beginner-guide"
              className="w-full text-center py-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors block"
            >
              {t('common:cta.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-12">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-amber-600 text-xl" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  {t('common:safety.title')}
                </h2>
                <p className="text-sm md:text-base text-gray-500 leading-relaxed mb-6">
                  {t('common:safety.desc')}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  {t('common:nav.safety')}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('common:finalCta.title')}
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto mb-8 text-sm md:text-base">
            {t('common:finalCta.desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20"
            >
              {t('common:cta.explore')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-8 py-4 rounded-lg border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              {t('common:cta.visitMain')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}